import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Modal from '@/components/common/Modal';
import BrandLogo from '@/components/common/BrandLogo';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { type AuthView, closeAuthModal, openAuthModal, setAuthView } from '@/store/slices/uiSlice';
import { isGuestUser } from '@/utils';
import { Icons } from '@/utils/Icons';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';

const TABS: { view: AuthView; label: string }[] = [
  { view: 'login', label: 'Log in' },
  { view: 'signup', label: 'Sign up' },
];

/**
 * Login / signup popup, opened from anywhere with `openAuthModal('login' | 'signup')`. There
 * are no login / signup pages. A referral link (`?ref=CODE` on any page, e.g. `/home?ref=CODE`)
 * opens it on signup with the code filled in. Rendered once, in App.
 */
const AuthModal = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { view, returnTo, referralCode } = useAppSelector((state) => state.ui.authModal);

  const close = () => dispatch(closeAuthModal());
  const done = () => {
    close();
    if (returnTo) navigate(returnTo);
  };
  const show = (next: AuthView) => dispatch(setAuthView(next));

  // Referral links: open signup with the code, then drop `ref` from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkReferralCode = searchParams.get('ref');
  useEffect(() => {
    if (!linkReferralCode) return;
    if (isGuestUser()) {
      dispatch(openAuthModal({ view: 'signup', referralCode: linkReferralCode }));
    }
    setSearchParams(
      (params) => {
        params.delete('ref');
        return params;
      },
      { replace: true }
    );
  }, [linkReferralCode, dispatch, setSearchParams]);

  // The backdrop doesn't close the popup (so a stray tap can't wipe the form), but Escape does.
  const isOpen = view !== null;
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) =>
      event.key === 'Escape' && dispatch(closeAuthModal());
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, dispatch]);

  return (
    <Modal
      open={isOpen}
      onClose={close}
      size="md"
      showClose={false}
      closeOnBackdrop={false}
      bodyClassName=""
    >
      <div className="relative p-5 sm:p-6">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-full text-shell-muted transition hover:bg-shell-raised hover:text-shell-fg"
        >
          <Icons.Cross size={18} />
        </button>

        <BrandLogo className="mx-auto mb-5 h-12 w-auto object-contain" />

        <div
          role="tablist"
          aria-label="Log in or sign up"
          className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-shell-raised p-1"
        >
          {TABS.map((tab) => (
            <button
              key={tab.view}
              type="button"
              role="tab"
              aria-selected={view === tab.view}
              onClick={() => show(tab.view)}
              className={`h-9 rounded-md text-sm font-semibold transition ${
                view === tab.view
                  ? 'bg-gold text-gold-foreground'
                  : 'text-shell-muted hover:text-shell-fg'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {view === 'signup' ? (
          <SignupForm referralCode={referralCode} onDone={done} onLogin={() => show('login')} />
        ) : (
          <LoginForm onDone={done} onSignup={() => show('signup')} />
        )}
      </div>
    </Modal>
  );
};

export default AuthModal;
